import React, { useState } from 'react';
import { Pencil } from 'lucide-react';
import { formatDateTime } from '@dashboard/lib/format-date';
import { getImageSrcAndSrcSet } from '@dashboard/services/getUrlFromCdn';
import getProductPriceLabel from '@dashboard/services/translation/getProductPriceLabel';
import clsx from 'clsx';
import checkIsEventSoldOut from '@dashboard/services/event/checkIsEventSoldOut';
import StatusBadge from '@/dashboard/components/StatusBadge';
import EventWhenWhere from '@dashboard/components/EventWhenWhere';
import { Badge } from '@dashboard/components/ui/badge';
import Tooltip from '@dashboard/components/Tooltip';
import EditPageNameDialog from '@dashboard/components/EditPageNameDialog';
import getPageTheme from '@repo/checkout/lib/theming/getPageTheme';

// Cards arrive wrapped in fragments by billing type, so count what is inside them.
const countCards = children =>
  React.Children.toArray(children).reduce(
    (count, child) =>
      count + (child.type === React.Fragment ? countCards(child.props.children) : 1),
    0,
  );

// Sized by the content column, which the rail and page sidebar narrow on a tablet.
const COLUMNS = {
  1: '',
  2: '@md:grid-cols-2',
  3: '@md:grid-cols-2 @3xl:grid-cols-3',
  4: '@md:grid-cols-2 @4xl:grid-cols-4',
};

const PageOverviewHeader = ({ page, side, children }) => {
  const { eventDetails, product } = page;
  const isEvent = page.type === 'event';
  const isCheckout = page.type === 'checkout';
  const isForm = page.type === 'form';

  const theme = getPageTheme(page);
  const title = isEvent || isForm ? page.title : page.productTitle;
  const [renameOpen, setRenameOpen] = useState(false);
  // The seller's own name for the page leads only when it says something the title doesn't.
  const showName = Boolean(page.name && page.name !== title);
  // The same image the page lists show as the page's thumbnail.
  const imageUrl = (isCheckout ? product?.media : page.images)?.[0]?.url;

  return (
    <div className="@container grid gap-2">
      {renameOpen && (
        <EditPageNameDialog isOpen onClose={() => setRenameOpen(false)} checkout={page} />
      )}
      <div className="border border-gray-200 dark:border-border rounded p-4 flex items-center gap-4">
        {imageUrl && (
          <img
            data-page-image
            alt=""
            className="size-16 shrink-0 self-start rounded object-cover bg-gray-50 dark:bg-card"
            {...getImageSrcAndSrcSet({ url: imageUrl, width: 128, height: 128 })}
          />
        )}
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-y-0.5">
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
            <div className="min-w-0">
              <div
                className="mb-1 flex items-center gap-1 text-xs text-gray-500 dark:text-muted-foreground"
                data-page-name-row
              >
                {showName && <span data-page-name>{page.name}</span>}
                <Tooltip label="Rename" asChild>
                  <button
                    type="button"
                    aria-label="Rename"
                    onClick={() => setRenameOpen(true)}
                    className={`-my-0.5 inline-flex items-center gap-1 rounded px-1 py-0.5 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-accent dark:hover:text-foreground ${showName ? '' : '-ml-1'}`}
                  >
                    <Pencil className="size-3" />
                    {!showName && 'Rename'}
                  </button>
                </Tooltip>
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <div className="text-lg leading-tight font-semibold">{title}</div>
                {page.status === 'archived' && <Badge variant="info">Archived</Badge>}
              </div>
            </div>
            {page.createdAt && (
              <div className="shrink-0 pt-1.5 text-xs text-gray-500 dark:text-muted-foreground">
                Created {formatDateTime(page.createdAt)}
              </div>
            )}
          </div>

          {isEvent && (
            <>
              <div className="space-y-1 text-sm leading-6">
                <EventWhenWhere eventDetails={eventDetails} />
                <div className="mt-2">
                  {page.eventDetails?.status === 'canceled' ? (
                    <StatusBadge status="canceled" label="Canceled" />
                  ) : checkIsEventSoldOut(page) ? (
                    <StatusBadge status="sold_out" label="Sold out"></StatusBadge>
                  ) : null}
                </div>
              </div>
            </>
          )}

          {isCheckout && (
            <div className="text-lg leading-tight">
              {getProductPriceLabel(product, theme.locale)}
            </div>
          )}
        </div>
      </div>

      {/* Trend cards, each with its chart, in one row; ratios and referrers in a row beneath. */}
      <div className={clsx('grid gap-2', COLUMNS[Math.min(countCards(children), 3)])}>
        {children}
      </div>
      {side && (
        <div className={clsx('grid gap-2', COLUMNS[Math.min(countCards(side), 4)])}>{side}</div>
      )}
    </div>
  );
};

export default PageOverviewHeader;
